import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserDataList } from '../models/auth.model';

export interface AuthState {
  user: UserDataList | null;
  sessionId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  sessionId: null,
  isAuthenticated: false,
  isLoading: false,
  error: null
};

@Injectable({
  providedIn: 'root'
})
export class AuthStoreService {
  private readonly _state = new BehaviorSubject<AuthState>(initialState);
  
  // Observables públicos para suscribirse
  public readonly state$: Observable<AuthState> = this._state.asObservable();
  public readonly user$: Observable<UserDataList | null> = new BehaviorSubject<UserDataList | null>(null);
  public readonly isAuthenticated$: Observable<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isLoading$: Observable<boolean> = new BehaviorSubject<boolean>(false);
  public readonly error$: Observable<string | null> = new BehaviorSubject<string | null>(null);

  constructor() {
    // Inicializar desde localStorage si existe sesión
    this.initializeFromLocalStorage();
  }

  // Getters para obtener el estado actual
  get currentState(): AuthState {
    return this._state.value;
  }

  get currentUser(): UserDataList | null {
    return this._state.value.user;
  }

  get isAuthenticated(): boolean {
    return this._state.value.isAuthenticated;
  }

  get sessionId(): string | null {
    return this._state.value.sessionId;
  }

  /**
   * Actualiza el estado completo
   */
  private setState(newState: Partial<AuthState>): void {
    const currentState = this._state.value;
    const updatedState = { ...currentState, ...newState };
    this._state.next(updatedState);
    
    // Actualizar observables individuales
    (this.user$ as BehaviorSubject<UserDataList | null>).next(updatedState.user);
    (this.isAuthenticated$ as BehaviorSubject<boolean>).next(updatedState.isAuthenticated);
    (this.isLoading$ as BehaviorSubject<boolean>).next(updatedState.isLoading);
    (this.error$ as BehaviorSubject<string | null>).next(updatedState.error);
  }

  /**
   * Establecer estado de carga
   */
  setLoading(isLoading: boolean): void {
    this.setState({ isLoading, error: null });
  }

  /**
   * Establecer error
   */
  setError(error: string): void {
    this.setState({ error, isLoading: false });
  }

  /**
   * Limpiar error
   */
  clearError(): void {
    this.setState({ error: null });
  }

  /**
   * Login exitoso - guardar usuario y sesión
   */
  setAuthData(userData: UserDataList, sessionId: string): void {
    this.setState({
      user: userData,
      sessionId,
      isAuthenticated: true,
      isLoading: false,
      error: null
    });
  }

  /**
   * Actualizar datos del usuario
   */
  updateUser(userData: Partial<UserDataList>): void {
    const currentUser = this._state.value.user;
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      this.setState({ user: updatedUser });
    }
  }

  /**
   * Logout - limpiar estado
   */
  clearAuth(): void {
    this.setState(initialState);
  }

  /**
   * Inicializar desde localStorage (recuperar sesión)
   */
  private initializeFromLocalStorage(): void {
    try {
      const sessionID = localStorage.getItem('sessionID');
      const userDataStr = localStorage.getItem('userData');
      
      if (sessionID && userDataStr) {
        const userData = JSON.parse(userDataStr) as UserDataList;
        this.setState({
          user: userData,
          sessionId: sessionID,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
      }
    } catch (error) {
      console.error('Error initializing auth from localStorage:', error);
      this.clearAuth();
    }
  }

  /**
   * Verificar si hay sesión activa
   */
  hasActiveSession(): boolean {
    return this.isAuthenticated && !!this.sessionId;
  }

  /**
   * Obtener token de sesión actual
   */
  getSessionToken(): string | null {
    return this.sessionId;
  }
}





